import React from "react";

export interface SessionEntry {
    instanceId: string;
    methodType: string;
    total: string;
    poolId: string;
    createdAt: string;
}

interface SessionHistoryProps {
    sessions: SessionEntry[];
    sessionsLoading: boolean;
    sessionsError: string | null;
    sessionsPage: number;
    sessionsTotal: number;
    sessionsPageSize: number;
    setSessionsPage: (page: number | ((p: number) => number)) => void;
    quoteSymbol: string;
}

const SessionHistory: React.FC<SessionHistoryProps> = ({
    sessions,
    sessionsLoading,
    sessionsError,
    sessionsPage,
    sessionsTotal,
    sessionsPageSize,
    setSessionsPage,
    quoteSymbol,
}) => {
    return (
        <div className="glass-card session-history-card" style={{
            borderRadius: "16px",
            marginTop: "20px",
            overflow: "hidden",
        }}>
            <div style={{
                padding: "16px 20px",
                background: "rgba(255,255,255,0.05)",
                borderBottom: "1px solid rgba(255,255,255,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
            }}>
                <h3 style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "white",
                }}>
                    Session History
                </h3>
                {sessionsLoading && (
                    <span style={{ color: "#9ca3af", fontSize: "12px" }}>Loading...</span>
                )}
            </div>
            <div style={{ padding: "16px 20px" }}>
                {sessionsError && (
                    <p style={{ color: "#ff6b6b", marginBottom: "8px" }}>{sessionsError}</p>
                )}
                {!sessionsLoading && sessions.length === 0 && (
                    <p style={{ color: "#9ca3af" }}>No sessions yet.</p>
                )}
                {!sessionsLoading && sessions.length > 0 && (
                    <div className="session-table-wrapper" style={{
                        overflowX: "auto",
                        WebkitOverflowScrolling: "touch",
                        position: "relative",
                    }}>
                        {/* Scroll fade indicator - shown on mobile */}
                        <div className="scroll-fade-indicator" style={{
                            display: "none",
                            position: "absolute",
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: "40px",
                            background: "linear-gradient(to right, transparent, rgba(26, 26, 36, 0.95))",
                            pointerEvents: "none",
                            zIndex: 5,
                        }} />
                        <div className="session-table" style={{ display: "grid", gap: "8px", minWidth: "fit-content" }}>
                            {sessions.map((session, index) => (
                                <div
                                    key={`${session.createdAt}-${index}`}
                                    className="session-row"
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "100px 80px 180px",
                                        gap: "12px",
                                        padding: "12px",
                                        borderRadius: "12px",
                                        background: "rgba(255,255,255,0.03)",
                                        border: "1px solid rgba(255,255,255,0.06)",
                                        color: "#d1d5db",
                                        fontSize: "14px",
                                    }}
                                >
                                    <div className="session-type" style={{
                                        textTransform: "capitalize",
                                        color: "white",
                                        fontWeight: 500,
                                        whiteSpace: "nowrap",
                                    }}>
                                        {session.methodType}
                                    </div>
                                    <div className="session-amount" style={{ whiteSpace: "nowrap" }}>
                                        {session.total} {quoteSymbol}
                                    </div>
                                    <div className="session-date" style={{ color: "#9ca3af", textAlign: "right", whiteSpace: "nowrap" }}>
                                        {new Date(session.createdAt).toLocaleString()}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {sessionsTotal > sessionsPageSize && (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: "12px",
                        color: "#9ca3af",
                        fontSize: "12px",
                    }}>
                        <span>
                            Page {sessionsPage} of {Math.max(1, Math.ceil(sessionsTotal / sessionsPageSize))}
                        </span>
                        <div style={{ display: "flex", gap: "8px" }}>
                            <button
                                onClick={() => setSessionsPage((p) => Math.max(1, p - 1))}
                                disabled={sessionsPage === 1}
                                style={{
                                    padding: "6px 10px",
                                    borderRadius: "8px",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    background: "rgba(255,255,255,0.05)",
                                    color: sessionsPage === 1 ? "#6b7280" : "white",
                                    cursor: sessionsPage === 1 ? "not-allowed" : "pointer",
                                }}
                            >
                                Prev
                            </button>
                            <button
                                onClick={() =>
                                    setSessionsPage((p) =>
                                        Math.min(Math.ceil(sessionsTotal / sessionsPageSize), p + 1)
                                    )
                                }
                                disabled={sessionsPage >= Math.ceil(sessionsTotal / sessionsPageSize)}
                                style={{
                                    padding: "6px 10px",
                                    borderRadius: "8px",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    background: "rgba(255,255,255,0.05)",
                                    color:
                                        sessionsPage >= Math.ceil(sessionsTotal / sessionsPageSize)
                                            ? "#6b7280"
                                            : "white",
                                    cursor:
                                        sessionsPage >= Math.ceil(sessionsTotal / sessionsPageSize)
                                            ? "not-allowed"
                                            : "pointer",
                                }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SessionHistory;
